import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "~/shared/api/client";
import {
    projectsKeys,
    type AddExistingMemberPayload,
    type InviteMembersPayload,
    type ProjectMember,
    type ProjectMemberStatus,
    type UpdateMemberPayload,
} from "./api-types";

// ---------------------------------------------------------------------------
// Fetch functions — project members
// ---------------------------------------------------------------------------

export async function listProjectMembers(
    projectId: string,
    status?: ProjectMemberStatus,
): Promise<ProjectMember[]> {
    const qs = status ? `?status=${status}` : "";
    return apiGet<ProjectMember[]>(`/v2/projects/${projectId}/members${qs}`);
}

export async function inviteProjectMembers(
    projectId: string,
    payload: InviteMembersPayload,
): Promise<ProjectMember[]> {
    return apiPost<ProjectMember[]>(`/v2/projects/${projectId}/members/invite`, payload);
}

export async function addExistingProjectMember(
    projectId: string,
    payload: AddExistingMemberPayload,
): Promise<ProjectMember> {
    return apiPost<ProjectMember>(`/v2/projects/${projectId}/members/add`, payload);
}

export async function updateProjectMember(
    projectId: string,
    memberId: string,
    payload: UpdateMemberPayload,
): Promise<ProjectMember> {
    return apiPatch<ProjectMember>(`/v2/projects/${projectId}/members/${memberId}`, payload);
}

export async function removeProjectMember(projectId: string, memberId: string): Promise<void> {
    await apiDelete<{ message: string }>(`/v2/projects/${projectId}/members/${memberId}`);
}

// ---------------------------------------------------------------------------
// Query + mutation hooks
// ---------------------------------------------------------------------------

export function useProjectMembers(projectId: string | undefined, status?: ProjectMemberStatus) {
    return useQuery<ProjectMember[], Error>({
        queryKey: projectId
            ? [...projectsKeys.members(projectId), status ?? "all"]
            : ["projects", "members", "skip"],
        queryFn: () => listProjectMembers(projectId!, status),
        enabled: Boolean(projectId),
    });
}

export function useInviteProjectMembers(projectId: string) {
    const qc = useQueryClient();
    return useMutation<ProjectMember[], Error, InviteMembersPayload>({
        mutationFn: (payload) => inviteProjectMembers(projectId, payload),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
        },
    });
}

export function useAddExistingProjectMember(projectId: string) {
    const qc = useQueryClient();
    return useMutation<ProjectMember, Error, AddExistingMemberPayload>({
        mutationFn: (payload) => addExistingProjectMember(projectId, payload),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
        },
    });
}

export function useUpdateProjectMember(projectId: string) {
    const qc = useQueryClient();
    return useMutation<ProjectMember, Error, { memberId: string; payload: UpdateMemberPayload }>({
        mutationFn: ({ memberId, payload }) => updateProjectMember(projectId, memberId, payload),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
        },
    });
}

export function useRemoveProjectMember(projectId: string) {
    const qc = useQueryClient();
    return useMutation<void, Error, string>({
        mutationFn: (memberId) => removeProjectMember(projectId, memberId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: projectsKeys.members(projectId) });
        },
    });
}
